import React, { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { useLoading } from '../hooks/useLoading';
import { Topbar } from '../components/Topbar';
import { 
  IdCard, QrCode, Shield, Share2, Download, 
  User, GraduationCap, ChevronRight, RefreshCcw,
  Camera, ExternalLink, CheckCircle2, ZoomIn, ZoomOut,
  Maximize2, X, Check, Trash2, GripHorizontal
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button, Badge, Skeleton } from '../components/ui';

export function StudentIDPage() {
  const { user, updateUser } = useAuth();
  const { allEnrollments, professors, updateStudent, updateProfessor } = useData();
  const navigate = useNavigate();
  const [error, setError] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editorSource, setEditorSource] = useState(null);
  const loading = useLoading(600);

  // Editor States
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const imageRef = useRef(null);
  const containerRef = useRef(null);

  // Identificar se é Aluno ou Professor
  const isProfessor = user?.type === 'professor';

  // Buscar os dados conforme o tipo de usuário
  const enrollment = !isProfessor ? (allEnrollments || []).find(e => 
    e.enrollmentId === user?.enrollmentId || e.citizenId === user?.citizenId
  ) : null;

  const professorData = isProfessor ? (professors || []).find(p => 
    p.enrollmentId === user?.enrollmentId || p.email === user?.email
  ) : null;

  // Dados unificados para exibição
  const profileData = isProfessor ? professorData : enrollment;
  const userName = profileData?.fullName || user?.name || (isProfessor ? 'Professor' : 'Estudante');
  const idNumber = profileData?.enrollmentId || user?.enrollmentId || 'REF-000000';
  const profilePicture = profileData?.profilePicture || null;

  // Campos específicos
  const subTitle = isProfessor ? 'Registro Funcional' : 'Matrícula';
  const departmentLabel = isProfessor ? 'Departamento / Curso' : 'Curso Acadêmico';
  const departmentValue = isProfessor 
    ? (profileData?.courses || 'Docente Institucional') 
    : (profileData?.course || profileData?.courseName || 'Estudante');
  const secondaryLabel = isProfessor ? 'Cargo' : 'Turma';
  const secondaryValue = isProfessor ? 'Professor' : `TURMA ${profileData?.turma?.replace(/Turma\s+/i, '') || 'N/A'}`;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      alert('Formato de arquivo inválido. Use JPG, PNG ou WEBP.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setEditorSource(reader.result);
      setIsEditorOpen(true);
      setZoom(1);
      setPosition({ x: 0, y: 0 });
    };
    reader.readAsDataURL(file);
  };

  const handleApplyCrop = () => {
    if (!imageRef.current || !containerRef.current) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Proporção 3:4 (estilo retrato institucional)
    const targetWidth = 450;
    const targetHeight = 600;
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const img = imageRef.current;
    const cropBox = containerRef.current.getBoundingClientRect();
    const imgRect = img.getBoundingClientRect();

    const scale = img.naturalWidth / imgRect.width;
    
    const sourceX = (cropBox.left - imgRect.left) * scale;
    const sourceY = (cropBox.top - imgRect.top) * scale;
    const sourceWidth = cropBox.width * scale;
    const sourceHeight = cropBox.height * scale;

    ctx.drawImage(
      img,
      sourceX, sourceY, sourceWidth, sourceHeight,
      0, 0, targetWidth, targetHeight
    );

    const croppedImage = canvas.toDataURL('image/jpeg', 0.85);
    setPreviewImage(croppedImage);
    setIsEditorOpen(false);
    setEditorSource(null);
  };

  const handleSavePhoto = async () => {
    if (!previewImage) return;
    
    setIsUpdating(true);
    try {
      if (isProfessor) {
        updateProfessor(idNumber, { profilePicture: previewImage });
      } else {
        updateStudent(idNumber, { profilePicture: previewImage });
      }
      
      updateUser({ profilePicture: previewImage });
      setPreviewImage(null);
      setIsUpdating(false);
    } catch (err) {
      alert('Não foi possível atualizar sua foto.');
      setIsUpdating(false);
    }
  };

  const handleCancelPhoto = () => {
    setPreviewImage(null);
  };

  return (
    <div className="min-h-screen bg-[#030712]">
      <Topbar />
      
      <div className="flex flex-col items-center justify-center py-12 px-6">
        {loading ? (
          <Skeleton className="w-full max-w-[420px] h-[650px] rounded-[48px]" />
        ) : error ? (
          <div className="text-center bg-[#0a1120] border border-[#1d2a42] p-12 rounded-[3rem] shadow-2xl max-w-md">
            <div className="w-20 h-20 bg-red/10 rounded-[2rem] flex items-center justify-center mx-auto mb-8 text-red">
              <RefreshCcw size={44} />
            </div>
            <h2 className="text-3xl font-black mb-4 tracking-tighter uppercase">Erro de Carregamento</h2>
            <p className="text-muted mb-10 leading-relaxed font-medium">
              Não foi possível carregar sua carteirinha institucional neste momento.
            </p>
            <Button 
              className="w-full py-5 rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-red/20 bg-red hover:bg-red/80" 
              onClick={() => window.location.reload()}
            >
              Tentar Novamente
            </Button>
          </div>
        ) : !profileData ? (
          <div className="text-center bg-[#0a1120] border border-[#1d2a42] p-12 rounded-[3rem] shadow-2xl max-w-md">
            <div className="w-20 h-20 bg-yellow/10 rounded-[2rem] flex items-center justify-center mx-auto mb-8 text-yellow">
              <IdCard size={44} />
            </div>
            <h2 className="text-3xl font-black mb-4 tracking-tighter uppercase">Identificação Pendente</h2>
            <p className="text-muted mb-10 leading-relaxed font-medium">
              {isProfessor 
                ? 'Seu registro de docente ainda não foi localizado. Entre em contato com a secretaria.'
                : 'Você ainda não possui uma matrícula ativa. Realize sua matrícula para emitir sua carteirinha acadêmica digital.'}
            </p>
            {!isProfessor && (
              <Button 
                className="w-full py-5 rounded-2xl font-black text-sm uppercase tracking-widest shadow-xl shadow-purple/20" 
                onClick={() => navigate('/cursos')}
              >
                Realizar Matrícula
              </Button>
            )}
          </div>
        ) : (
          <div className="w-full max-w-[420px] flex flex-col gap-10">
            {/* Carteirinha Digital Institucional */}
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              className="relative aspect-[1/1.6] bg-[#0a1120] rounded-[32px] overflow-hidden shadow-[0_50px_100px_-20px_rgba(0,0,0,0.8)] border border-white/5 group"
            >
              {/* Decorative Elements */}
              <div className="absolute inset-0 bg-gradient-to-b from-purple/5 to-black/80 pointer-events-none" />
              
              {/* Institutional Header */}
              <div className="relative px-8 pt-10 pb-6 text-center">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center shadow-2xl">
                    <GraduationCap className="text-black" size={32} />
                  </div>
                  <h2 className="text-2xl font-black tracking-[4px] text-white mt-2">LUMENFALL</h2>
                  <span className="text-[10px] uppercase tracking-[4px] text-purple font-black">University Portal</span>
                </div>
              </div>

              {/* Profile Section */}
              <div className="relative flex flex-col items-center px-8">
                <div className="relative mb-8">
                  <label className="relative block cursor-pointer group/photo">
                    <input 
                      type="file" 
                      className="hidden" 
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleFileChange}
                      disabled={isUpdating}
                    />
                    
                    {/* Moldura Fixa: 220x220 Desktop (ajustado para proporção no card) */}
                    <div className="w-[120px] h-[120px] sm:w-[220px] sm:h-[220px] rounded-2xl border-4 border-white/10 p-1.5 bg-[#030712] shadow-2xl overflow-hidden group-hover:border-purple/50 transition-all duration-500">
                      <div className="w-full h-full bg-[#111827] rounded-xl flex items-center justify-center text-white shadow-inner relative overflow-hidden">
                        {previewImage ? (
                          <img src={previewImage} alt="Preview" className="w-full h-full object-cover" />
                        ) : profilePicture ? (
                          <img src={profilePicture} alt="Profile" className="w-full h-full object-cover" />
                        ) : (
                          <div className="flex flex-col items-center gap-2 opacity-20">
                            <User size={64} className="sm:w-24 sm:h-24 w-12 h-12" />
                          </div>
                        )}
                        
                        <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center opacity-0 group-hover/photo:opacity-100 transition-all duration-300 backdrop-blur-[2px]">
                          <Camera size={28} className="text-white mb-2" />
                          <span className="text-[9px] font-black uppercase tracking-[3px] text-white">Alterar Foto</span>
                        </div>

                        {isUpdating && (
                          <div className="absolute inset-0 bg-black/80 flex items-center justify-center">
                            <RefreshCcw size={32} className="text-purple animate-spin" />
                          </div>
                        )}
                      </div>
                    </div>
                  </label>
                </div>

                <div className="text-center space-y-8 mb-10 w-full">
                  {/* Nome */}
                  <div className="flex flex-col items-center gap-1.5">
                    <span className="text-[10px] uppercase tracking-[4px] text-purple font-black">NOME COMPLETO</span>
                    <h3 className="text-2xl font-black tracking-tight text-white uppercase leading-tight px-4">{userName}</h3>
                  </div>
                  
                  {/* Grid de ID e Secundaria (Turma ou Cargo) */}
                  <div className="grid grid-cols-2 gap-px bg-white/5 py-6 border-y border-white/5">
                    <div className="flex flex-col items-center gap-1.5 border-r border-white/5">
                      <span className="text-[9px] uppercase tracking-[3px] text-muted font-black">{subTitle}</span>
                      <strong className="text-sm font-mono text-white font-black">{idNumber}</strong>
                    </div>
                    <div className="flex flex-col items-center gap-1.5">
                      <span className="text-[9px] uppercase tracking-[3px] text-muted font-black">{secondaryLabel}</span>
                      <strong className="text-sm text-white font-black">{secondaryValue}</strong>
                    </div>
                  </div>

                  {/* Departamento ou Curso */}
                  <div className="flex flex-col items-center gap-1.5 pt-2">
                    <span className="text-[9px] uppercase tracking-[3px] text-muted font-black">{departmentLabel}</span>
                    <strong className="text-base text-purple font-black uppercase tracking-widest">{departmentValue}</strong>
                  </div>
                </div>
              </div>

              {/* QR Code Validation Area */}
              <div className="px-8 pb-8">
                <div className="bg-white rounded-[2rem] p-6 flex flex-col items-center gap-4 shadow-2xl relative">
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${idNumber}&color=000000&bgcolor=ffffff`}
                    alt="Institutional QR Code"
                    className="w-32 h-32"
                  />
                  <div className="text-center">
                    <Badge variant={isProfessor ? 'purple' : 'green'} className="px-4 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border">
                      {isProfessor ? 'Corpo Docente' : 'Aluno Regular'}
                    </Badge>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <div className="space-y-4">
              <AnimatePresence>
                {previewImage && (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 10 }}
                    className="flex flex-col gap-4"
                  >
                    <Button 
                      onClick={handleSavePhoto}
                      disabled={isUpdating}
                      className="h-16 bg-purple hover:bg-purple/90 text-white rounded-2xl font-black text-sm uppercase tracking-[2px] flex items-center justify-center gap-3 shadow-2xl shadow-purple/20 transition-all active:scale-95"
                    >
                      {isUpdating ? <RefreshCcw size={20} className="animate-spin" /> : <Check size={20} />}
                      Salvar Alteração
                    </Button>
                    <Button 
                      onClick={handleCancelPhoto}
                      disabled={isUpdating}
                      variant="outline"
                      className="h-16 border-white/10 hover:bg-white/5 text-white rounded-2xl font-black text-sm uppercase tracking-[2px] transition-all active:scale-95"
                    >
                      Cancelar
                    </Button>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="grid grid-cols-2 gap-4">
                <Button 
                  variant="outline" 
                  className="h-14 border-white/10 hover:bg-white/5 rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-3 opacity-50 cursor-not-allowed"
                >
                  <Download size={18} /> Baixar
                </Button>
                <Button 
                  variant="outline" 
                  className="h-14 border-white/10 hover:bg-white/5 rounded-2xl font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-3 opacity-50 cursor-not-allowed"
                >
                  <Share2 size={18} /> Compartilhar
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Photo Adjustment Editor Modal - Portal com Z-Index Máximo e CSS Forçado */}
      {isEditorOpen && createPortal(
        <div 
          style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            width: '100vw', 
            height: '100vh', 
            zIndex: 2147483647,
            backgroundColor: '#000000',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            userSelect: 'none'
          }}
        >
          {/* Header */}
          <div style={{ height: '70px', padding: '0 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)', background: '#000' }}>
            <button onClick={() => { setIsEditorOpen(false); setEditorSource(null); }} style={{ color: '#fff', background: 'none', border: 'none', cursor: 'pointer' }}><X size={28} /></button>
            <div style={{ textAlign: 'center' }}>
              <div style={{ color: '#fff', fontWeight: '900', fontSize: '14px', letterSpacing: '4px', textTransform: 'uppercase' }}>Editor de Foto</div>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '10px', fontWeight: '700' }}>3 : 4 RETRATO INSTITUCIONAL</div>
            </div>
            <button onClick={handleApplyCrop} style={{ width: '44px', height: '44px', background: '#fff', color: '#000', borderRadius: '50%', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}><Check size={24} /></button>
          </div>

          {/* Viewport */}
          <div style={{ flex: 1, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0a', overflow: 'hidden' }}>
            
            {/* Image Layer (Draggable) */}
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {editorSource && (
                <motion.div
                  drag
                  dragElastic={0}
                  dragMomentum={false}
                  style={{ scale: zoom }}
                  className="touch-none"
                >
                  <img 
                    ref={imageRef}
                    src={editorSource}
                    alt="Target"
                    style={{
                      maxHeight: '80vh',
                      maxWidth: '80vw',
                      pointerEvents: 'none',
                      display: 'block'
                    }}
                  />
                </motion.div>
              )}
            </div>

            {/* Overlay Layer (Mask with Hole) */}
            <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {/* This div creates the transparent hole */}
              <div 
                ref={containerRef}
                style={{
                  width: '280px',
                  height: '373px',
                  border: '2px solid white',
                  boxShadow: '0 0 0 5000px rgba(0,0,0,0.8)',
                  position: 'relative'
                }}
              >
                {/* Corner Guides */}
                <div style={{ position: 'absolute', top: '-2px', left: '-2px', width: '20px', height: '20px', borderTop: '4px solid #fff', borderLeft: '4px solid #fff' }} />
                <div style={{ position: 'absolute', top: '-2px', right: '-2px', width: '20px', height: '20px', borderTop: '4px solid #fff', borderRight: '4px solid #fff' }} />
                <div style={{ position: 'absolute', bottom: '-2px', left: '-2px', width: '20px', height: '20px', borderBottom: '4px solid #fff', borderLeft: '4px solid #fff' }} />
                <div style={{ position: 'absolute', bottom: '-2px', right: '-2px', width: '20px', height: '20px', borderBottom: '4px solid #fff', borderRight: '4px solid #fff' }} />
              </div>
            </div>

            {/* Helper Text */}
            <div style={{ position: 'absolute', bottom: '20px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(0,0,0,0.6)', padding: '6px 16px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', fontSize: '10px', fontWeight: '900', letterSpacing: '2px', textTransform: 'uppercase' }}>
              Arraste para posicionar
            </div>
          </div>

          {/* Controls */}
          <div style={{ padding: '32px 24px', background: '#000', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '24px' }}>
            <div style={{ width: '100%', maxWidth: '400px', display: 'flex', alignItems: 'center', gap: '20px' }}>
              <ZoomOut size={20} color="rgba(255,255,255,0.4)" />
              <input 
                type="range" 
                min="0.5" 
                max="3" 
                step="0.01" 
                value={zoom} 
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                style={{ flex: 1, height: '4px', appearance: 'none', background: 'rgba(255,255,255,0.1)', borderRadius: '2px', cursor: 'pointer' }}
              />
              <ZoomIn size={20} color="rgba(255,255,255,0.4)" />
            </div>
            <div style={{ display: 'flex', gap: '12px', width: '100%', maxWidth: '320px' }}>
              <button onClick={handleApplyCrop} style={{ flex: 1, height: '50px', background: '#fff', color: '#000', border: 'none', borderRadius: '12px', fontWeight: '900', fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' }}>Confirmar</button>
              <button onClick={() => { setIsEditorOpen(false); setEditorSource(null); }} style={{ flex: 1, height: '50px', background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', fontWeight: '900', fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', cursor: 'pointer' }}>Cancelar</button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
